/* Pure helpers for the internal blog-design lab variants.
   Kept free of Nuxt imports so the logic can be unit-tested with plain node
   (see scripts/test-blog-designs.mjs). */

export interface BlogDesignVariant {
  id: string;
  label: string;
  blurb: string;
}

export const blogDesignVariants: BlogDesignVariant[] = [
  {
    id: 'magazine',
    label: 'Magazine feature',
    blurb:
      'Centered single reading column, display headline, drop cap, pull-quote takeaways, numbered chapters, captioned full-width screenshots.',
  },
  {
    id: 'guide',
    label: 'Docs / guide',
    blurb:
      'Three-column app-docs layout: sticky TOC rail left, dense step-styled prose center, takeaways + pricing CTA rail right. TOC collapses to a details panel on mobile.',
  },
  {
    id: 'briefing',
    label: 'Briefing, summary-first',
    blurb:
      'Title + lede + takeaway cards up top, shortlist table and verdict box pulled high, prose below with a mid-article related-read band and a closing CTA before the FAQ.',
  },
  {
    id: 'field-guide',
    label: 'Field guide (guide + briefing)',
    blurb:
      'Briefing-style top zone (compact hero, takeaway cards, one-line verdict strip) handing off to the guide three-rail layout with sticky TOC and pricing rail.',
  },
  {
    id: 'briefing-pro',
    label: 'Briefing pro (briefing + guide nav)',
    blurb:
      'Summary-first briefing body upgraded with guide navigation: slim sticky bar with reading progress and current section, chapter-numbered H2 flow, mid-article also-read band.',
  },
  {
    id: 'editorial',
    label: 'Editorial',
    blurb: 'Restrained editorial layout — content first, minimal chrome',
  },
];

export function resolveBlogDesignVariant(param: unknown): string {
  const id = String(param || 'magazine');
  return blogDesignVariants.some((variant) => variant.id === id) ? id : 'magazine';
}

interface TocLink {
  id?: string;
  depth?: number;
  text?: string;
}

export function tocLinksFrom(doc: any): TocLink[] {
  const links = doc?.body?.toc?.links || [];
  return links.filter((link: TocLink) => link?.depth === 2 && link.id && link.text);
}

/* Also-read band: split the rendered body just after the second H2 (same rule
   as pages/blog/[slug].vue). Shallow-copies the AST, never mutates the doc. */
export const ALSO_READ_AFTER_H2 = 2;

export function splitForAlsoRead(doc: any, afterH2 = ALSO_READ_AFTER_H2) {
  const children = doc?.body?.children;
  const link = (Array.isArray(doc?.related) ? doc.related : []).find(
    (item: any) => item?.title && item?.path,
  );
  if (!link || !Array.isArray(children) || children.length === 0) return null;

  let h2Count = 0;
  let splitIndex = -1;
  for (let index = 0; index < children.length; index += 1) {
    if (children[index]?.tag === 'h2') {
      h2Count += 1;
      if (h2Count === afterH2) {
        splitIndex = index + 1;
        break;
      }
    }
  }
  if (splitIndex <= 0 || splitIndex >= children.length) return null;

  return {
    before: { ...doc, body: { ...doc.body, children: children.slice(0, splitIndex) } },
    after: { ...doc, body: { ...doc.body, children: children.slice(splitIndex) } },
    link,
  };
}

/* Mid-article trial card: split just before the Nth H2 of the given doc so
   phone readers meet a trial button before the end matter. Skipped when the
   article already places its own `::inline-cta` before that point, or when
   fewer than two sections would follow (the end-of-article close is enough). */
const hasInlineCta = (node: any): boolean =>
  node?.tag === 'inline-cta' ||
  (Array.isArray(node?.children) && node.children.some(hasInlineCta));

export function splitForInlineCta(doc: any, beforeH2: number) {
  const children = doc?.body?.children;
  if (!Array.isArray(children) || children.length === 0 || beforeH2 < 1) return null;

  const h2Indexes: number[] = [];
  children.forEach((child: any, index: number) => {
    if (child?.tag === 'h2') h2Indexes.push(index);
  });
  if (h2Indexes.length < beforeH2 + 1) return null;

  const splitIndex = h2Indexes[beforeH2 - 1];
  if (children.slice(0, splitIndex).some(hasInlineCta)) return null;

  return {
    before: { ...doc, body: { ...doc.body, children: children.slice(0, splitIndex) } },
    after: { ...doc, body: { ...doc.body, children: children.slice(splitIndex) } },
  };
}

/* Lead-image hoisting (editorial variant): lift the first body image into the
   lead position — but only when its adjacent paragraphs do not reference it
   ("this is the screen…", "as shown", "above/below" anaphora). Tries the first
   two body images in order; returns the image plus a doc copy with the image
   node removed, or null when every candidate is context-bound. */

/* Explicit anaphora that anchors copy to an inline image. */
const IMAGE_ANAPHORA =
  /this is the (screen|view|page|workspace)|as shown|shown (above|below)|(above|below)\b[^.]{0,40}\b(screenshot|image|screen|figure)|the (screenshot|image|figure|screen) (above|below)/i;

const nodeText = (node: any): string => {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  if (Array.isArray(node.children)) return node.children.map(nodeText).join(' ');
  return '';
};

const imageNodeOf = (node: any) => {
  if (node?.tag === 'img') return node;
  if (!Array.isArray(node?.children)) return null;
  return node.children.find((child: any) => child?.tag === 'img') || null;
};

export function hoistLeadImage(doc: any, maxCandidates = 2) {
  // Walkthrough images must remain beside the section that explains them.
  if (doc?.keepImagesInline === true) return null;
  const children = doc?.body?.children;
  if (!Array.isArray(children)) return null;

  const imageIndexes: number[] = [];
  children.forEach((node: any, index: number) => {
    if (imageNodeOf(node)) imageIndexes.push(index);
  });

  for (const index of imageIndexes.slice(0, maxCandidates)) {
    const before = nodeText(children[index - 1]);
    const after = nodeText(children[index + 1]);
    if (IMAGE_ANAPHORA.test(before) || IMAGE_ANAPHORA.test(after)) continue;

    const img = imageNodeOf(children[index]);
    if (!img?.props?.src) continue;
    return {
      image: { src: img.props.src, alt: img.props.alt || '' },
      doc: {
        ...doc,
        body: { ...doc.body, children: [...children.slice(0, index), ...children.slice(index + 1)] },
      },
    };
  }
  return null;
}

/* Pull one H2 section (heading + everything up to the next H2) out of the body
   so a variant can feature it higher on the page. Matches on the heading id. */
export function extractBodySection(doc: any, idFragment: string) {
  const children = doc?.body?.children;
  if (!Array.isArray(children)) return null;

  const start = children.findIndex(
    (node: any) => node?.tag === 'h2' && String(node?.props?.id || '').includes(idFragment),
  );
  if (start < 0) return null;

  let end = children.length;
  for (let index = start + 1; index < children.length; index += 1) {
    if (children[index]?.tag === 'h2') {
      end = index;
      break;
    }
  }

  const slice = children.slice(start, end);
  if (slice.length === 0 || end - start === children.length) return null;

  const rest = [...children.slice(0, start), ...children.slice(end)];
  return {
    section: { ...doc, body: { ...doc.body, children: slice } },
    rest: { ...doc, body: { ...doc.body, children: rest } },
  };
}

/* Opening text stays ahead of artwork/contents without moving authored images.
   Split before the first heading or visual; preserve node order and metadata. */
export function splitIntroduction(doc: any) {
  const children = doc?.body?.children;
  if (!Array.isArray(children) || !children.length) return null;
  const isVisual = (node: any): boolean =>
    ['img', 'figure', 'blog-illustration', 'BlogIllustration'].includes(node?.tag) ||
    (Array.isArray(node?.children) && node.children.some(isVisual));
  let boundary = children.findIndex((node: any) => /^h[1-6]$/.test(node?.tag || '') || isVisual(node));
  if (boundary < 0) boundary = children.length;
  if (!boundary) return null;
  const slice = (nodes: any[]) => ({ ...doc, body: { ...doc.body, children: nodes } });
  return {
    intro: slice(children.slice(0, boundary)),
    rest: boundary < children.length ? slice(children.slice(boundary)) : null,
  };
}
